import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

const familyData = [
    {
        id: 1,
        title: "Parental Leave",
        description: "At Mobikode, family comes first. Our parental leave policy gives you the time and support to embrace life's most meaningful moments, helping you care for your growing family with confidence and peace of mind. "
    },
    {
        id: 2,
        title: "Bereavement Leave",
        description: "At Mobikode, we know that losing a loved one is never easy. Our bereavement leave gives you the time and support to be with family, honor cherished memories, and focus on healing when you need it most."
    },
    {
        id: 3,
        title: "Care of dependents",
        description: "At Mobikode, family always comes first. Whether you're caring for children, parents, or loved ones, our dependent care support helps you manage life's responsibilities with greater ease, flexibility, and peace of mind. "
    },
    {
        id: 4,
        title: "Flexible Working",
        description: "At Mobikode, we know life doesn’t stop when work begins. Our flexible working approach helps you support your family, manage personal responsibilities, and enjoy a healthier balance between career and home."
    }
];

const YourFamilyWellbeingSection = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);

    const [sliderRef, instanceRef] = useKeenSlider({
        initial: 0,
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
        breakpoints: {
            "(min-width: 640px)": {
                slides: { perView: 1.5, spacing: 20 },
            },
            "(min-width: 768px)": {
                slides: { perView: 2, spacing: 20 },
            },
            "(min-width: 1024px)": {
                slides: { perView: 3, spacing: 24 },
            },
        },
        slides: { perView: 1, spacing: 16 },
    });

    const fadeUpVariant = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    const isNextDisabled = () => {
        if (!instanceRef.current) return true;
        const totalSlides = instanceRef.current.track.details.slides.length;
        const perView = instanceRef.current.options.slides.perView;
        return currentSlide >= Math.ceil(totalSlides - perView);
    };

    return (
        <section className="w-full pb-[40px] md:pb-[100px] bg-white font-sora overflow-hidden">
            <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeUpVariant}
                    className="text-left max-w-[683px] mb-[32px] md:mb-[60px]"
                >
                    <h2 className="text-[20px] md:text-[30px] font-[600] text-[#01386A] tracking-tight mb-[12px] md:mb-[16px]">
                        Your family's well-being
                    </h2>
                    <p className="text-[#49577A] text-[14px] md:text-[16px] font-graphik font-[500] leading-[1.6]">
                        At Mobikode, we understand that every family journey is unique. Whether your family is growing or your caregiving responsibilities are evolving, we're committed to supporting you with flexibility, understanding, and benefits that help you thrive both personally and professionally.
                    </p>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    variants={fadeUpVariant}
                    className="relative"
                >
                    <div ref={sliderRef} className="keen-slider !overflow-visible sm:!overflow-hidden">
                        {familyData.map((item) => (
                            <div
                                key={item.id}
                                className="keen-slider__slide"
                            >
                                <div className="group flex flex-col bg-[#F0F0F0] hover:bg-[#01386A] p-[24px] sm:p-[30px] md:p-[40px] min-h-[240px] sm:min-h-[280px] md:min-h-[320px] h-full transition-colors duration-500 ease-in-out cursor-grab active:cursor-grabbing">
                                    <h3 className="text-[16px] md:text-[20px] font-[600] text-[#01386A] group-hover:text-white mb-[24px] sm:mb-[40px] transition-colors duration-500">
                                        {item.title}
                                    </h3>

                                    <p className="text-[#49577A] text-[14px] md:text-[16px] font-graphik font-[500] leading-[1.6] group-hover:text-white transition-colors duration-500 mt-auto">
                                        {item.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {loaded && instanceRef.current && (
                        <div className="flex items-center gap-4 mt-6 md:mt-8">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    instanceRef.current?.prev();
                                }}
                                disabled={currentSlide === 0}
                                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors duration-300 ${currentSlide === 0
                                    ? 'border-gray-300 text-gray-300 cursor-not-allowed'
                                    : 'border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white'
                                    }`}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7" />
                                </svg>
                            </button>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    instanceRef.current?.next();
                                }}
                                disabled={isNextDisabled()}
                                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors duration-300 ${isNextDisabled()
                                    ? 'border-gray-300 text-gray-300 cursor-not-allowed'
                                    : 'border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white'
                                    }`}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    )}
                </motion.div>

            </div>
        </section>
    );
};

export default YourFamilyWellbeingSection;